import { useSettings } from '@/hooks/useSettings';
import { fromFeatures, toFeaturesPatch } from '@/pages/settings/userPreferences.mappers';
import type { UseUserPreferencesFormOptions, UserPreferencesValues } from '@/pages/settings/userPreferences.types';
import { useEffect } from 'react';
import { type PathValue, useForm } from 'react-hook-form';

/**
 * Settings-page preferences as a react-hook-form form. Saving patches ONLY the
 * fields the user edited, and a settings refetch (e.g. after another card
 * auto-saves) refreshes only the fields the user has not touched.
 */
export const useUserPreferencesForm = ({ enabled, onSaved, onSaveError }: UseUserPreferencesFormOptions) => {
  const { features, isLoading, isSaving, setFeatures } = useSettings({ enabled });
  const { watch, setValue, reset, getValues, formState } = useForm<UserPreferencesValues>({
    defaultValues: fromFeatures(features),
  });

  useEffect(() => {
    reset(fromFeatures(features), { keepDirtyValues: true });
  }, [features, reset]);

  const setField = <K extends keyof UserPreferencesValues>(name: K, value: UserPreferencesValues[K]) =>
    setValue(name, value as PathValue<UserPreferencesValues, K>, { shouldDirty: true });

  const save = () => {
    const values = getValues();
    const patch = toFeaturesPatch(values, formState.dirtyFields);
    if (Object.keys(patch).length === 0) {
      onSaved();
      return;
    }
    setFeatures(patch, {
      // Saved values become the new baseline; a field edited mid-save stays dirty with its new value.
      onSuccess: () => {
        const current = getValues();
        reset(values);
        for (const name of Object.keys(current) as (keyof UserPreferencesValues)[]) {
          if (current[name] !== values[name]) setField(name, current[name]);
        }
        onSaved();
      },
      onError: onSaveError,
    });
  };

  return { values: watch(), setField, save, isLoading, isSaving };
};
